import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Nav } from '@/components/Nav';
import { Footer } from '@/components/Footer';
import { api, VEHICLES, SERVICES } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Zap, Users } from 'lucide-react';

export default function Reservation() {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const prefill = location.state?.prefill || {};

    const [form, setForm] = useState({
        pickup_city: prefill.pickup_city || '',
        pickup_address: prefill.pickup_address || '',
        dropoff_city: prefill.dropoff_city || '',
        dropoff_address: prefill.dropoff_address || '',
        pickup_date: prefill.pickup_date || new Date().toISOString().slice(0, 10),
        pickup_time: prefill.pickup_time || '12:00',
        passengers: prefill.passengers || 1,
        vehicle_type: prefill.vehicle_type || VEHICLES[0].id,
        service_type: prefill.service_type || 'Courses urbaines',
        notes: '',
        contact_name: user?.name || '',
        contact_email: user?.email || '',
        contact_phone: '',
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(null);

    const currentVehicle = VEHICLES.find(v => v.id === form.vehicle_type) || VEHICLES[0];

    useEffect(() => {
        if (user) {
            setForm(f => ({ ...f, contact_name: f.contact_name || user.name, contact_email: f.contact_email || user.email }));
        }
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.pickup_city || !form.pickup_address || !form.dropoff_city || !form.dropoff_address || !form.contact_name || !form.contact_email || !form.contact_phone) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post('/bookings', { ...form });
            setSuccess(data);
            toast.success('Réservation confirmée !');
        } catch {
            toast.error('Erreur lors de la réservation');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        const startCheckout = async () => {
            try {
                const { data } = await api.post('/payments/checkout', {
                    booking_id: success.booking_id,
                    origin_url: window.location.origin,
                });
                window.location.href = data.checkout_url;
            } catch {
                toast.error('Erreur lors de l\'initialisation du paiement');
            }
        };
        return (
            <div className="bg-[#050505] min-h-screen text-[#F8F8F5]">
                <Nav />
                <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
                    <CheckCircle2 size={64} className="text-[#C5A059] mx-auto mb-8" />
                    <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">Confirmation</div>
                    <h1 className="font-display text-4xl md:text-5xl font-light mb-6">Votre réservation est <em className="text-[#C5A059] not-italic">prise en compte</em></h1>
                    <p className="text-[#A3A39D]">Référence : <span className="text-[#F8F8F5] font-mono">{success.booking_id.slice(0, 12).toUpperCase()}</span></p>
                    <p className="text-[#A3A39D] mt-2">Un email de confirmation a été envoyé à {success.contact_email}.</p>
                    <div className="mt-8 border border-[#C5A059]/30 bg-[#C5A059]/5 p-6 max-w-md mx-auto">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Acompte de réservation</div>
                        <div className="font-display text-5xl text-[#C5A059] mt-3">10,00 €</div>
                        <div className="text-xs text-[#A3A39D] mt-2">Non remboursable · déduit du prix de la course</div>
                        <div className="text-xs text-[#A3A39D] mt-1">Solde réglé au chauffeur : espèces · carte · chèque</div>
                        <button data-testid="pay-online-btn" onClick={startCheckout} className="mt-5 w-full bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#DCC283]">
                            Régler l'acompte 10 € (Stripe)
                        </button>
                    </div>
                    <div className="mt-8 flex gap-4 justify-center">
                        <button data-testid="reservation-home-btn" onClick={() => navigate('/')} className="border border-[#222] hover:border-[#C5A059] text-[#F8F8F5] px-8 py-3 text-xs uppercase tracking-[0.25em]">Accueil</button>
                        {user && (
                            <button data-testid="reservation-account-btn" onClick={() => navigate('/mon-compte')} className="bg-transparent border border-[#C5A059] text-[#C5A059] px-8 py-3 text-xs uppercase tracking-[0.25em]">Mon compte</button>
                        )}
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="bg-[#050505] min-h-screen text-[#F8F8F5]">
            <Nav />
            <div className="max-w-5xl mx-auto px-6 md:px-12 pt-32 pb-20">
                <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-4">Réservation</div>
                <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mb-12">
                    Configurer votre <span className="italic text-[#C5A059]">trajet</span>
                </h1>

                <form data-testid="reservation-form" onSubmit={submit} className="space-y-12">
                    {/* Service */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-[#A3A39D] mb-4">Type de service</div>
                        <select
                            data-testid="reservation-service-select"
                            value={form.service_type}
                            onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                            className="w-full md:w-1/2 bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 text-[#F8F8F5] outline-none [color-scheme:dark]"
                        >
                            {SERVICES.map(s => <option key={s.id} value={s.title} className="bg-[#111]">{s.title}</option>)}
                        </select>
                    </div>

                    {/* Itinerary */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Ville de départ *</label>
                            <input data-testid="res-pickup-city-input" value={form.pickup_city} onChange={(e) => setForm({ ...form, pickup_city: e.target.value })}
                                placeholder="Ex: Paris"
                                className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Ville d'arrivée *</label>
                            <input data-testid="res-dropoff-city-input" value={form.dropoff_city} onChange={(e) => setForm({ ...form, dropoff_city: e.target.value })}
                                placeholder="Ex: Lyon"
                                className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Adresse de départ *</label>
                            <input data-testid="res-pickup-input" value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                                className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Adresse d'arrivée *</label>
                            <input data-testid="res-dropoff-input" value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })}
                                className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Date *</label>
                            <input data-testid="res-date-input" type="date" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })}
                                className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none [color-scheme:dark]" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Heure *</label>
                            <input data-testid="res-time-input" type="time" value={form.pickup_time} onChange={(e) => setForm({ ...form, pickup_time: e.target.value })}
                                className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none [color-scheme:dark]" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Passagers</label>
                            <input data-testid="res-passengers-input" type="number" min="1" max={currentVehicle.maxPax} value={form.passengers} onChange={(e) => setForm({ ...form, passengers: Number(e.target.value) })}
                                className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                        </div>
                    </div>

                    {/* Vehicle */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-[#A3A39D] mb-4">Véhicule</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl">
                            {VEHICLES.map(v => (
                                <button type="button" key={v.id} data-testid={`res-vehicle-${v.id}`}
                                    onClick={() => setForm({ ...form, vehicle_type: v.id, passengers: Math.min(form.passengers, v.maxPax) })}
                                    className={`p-5 border text-left transition-colors flex items-start gap-3 ${form.vehicle_type === v.id ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-[#222] hover:border-[#C5A059]/40'}`}>
                                    {v.id === 'tesla_model_y'
                                        ? <Zap size={20} className="text-[#C5A059] mt-0.5" />
                                        : <Users size={20} className="text-[#C5A059] mt-0.5" />}
                                    <div>
                                        <div className="text-sm font-medium">{v.label}</div>
                                        <div className="text-xs text-[#A3A39D] mt-1">{v.desc}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Contact */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-4">Vos coordonnées</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Nom *</label>
                                <input data-testid="res-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                                    className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Email *</label>
                                <input data-testid="res-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                                    className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                            </div>
                            <div>
                                <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Téléphone *</label>
                                <input data-testid="res-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                                    className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none" />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-xs uppercase tracking-[0.25em] text-[#A3A39D]">Notes & demandes spéciales</label>
                        <textarea data-testid="res-notes-input" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            className="w-full bg-transparent border-b border-[#222] focus:border-[#C5A059] py-3 mt-2 text-[#F8F8F5] outline-none resize-none" />
                    </div>

                    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-6 pt-8 border-t border-white/5">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-[#A3A39D]">Acompte de réservation</div>
                            <div className="font-display text-4xl text-[#C5A059] mt-1">10,00 €</div>
                            <div className="text-xs text-[#A3A39D] mt-1">Non remboursable · déduit du prix de la course</div>
                        </div>
                        <button data-testid="res-submit-btn" type="submit" disabled={loading}
                            className="bg-[#C5A059] text-black px-10 py-4 uppercase tracking-[0.25em] text-xs font-medium hover:bg-[#DCC283] transition-colors flex items-center justify-center gap-3 disabled:opacity-50">
                            {loading ? <Loader2 className="animate-spin" size={16} /> : 'Confirmer la réservation'}
                        </button>
                    </div>
                </form>
            </div>
            <Footer />
        </div>
    );
}
